import * as React from 'react';
import { createRendererWithUniDriver } from '../../test/utils';
import { fileUploadDriverFactory } from './FileUpload.driver';
import { FileUpload, FileUploadProps } from '.';

describe('FileUpload', () => {
  const createDriver = createRendererWithUniDriver(fileUploadDriverFactory);

  const bootstrap = (props: Partial<FileUploadProps> = {}) => {
    return createDriver(
      <FileUpload
        label="file upload"
        buttonText="button text"
        files={[]}
        {...props}
      />,
    );
  };

  const file: any = { name: 'meme.png' };

  it('should render', async () => {
    const { driver } = bootstrap();
    expect(await driver.exists()).toBe(true);
  });

  it('should set lang to root', async () => {
    const lang = 'he';
    const { driver } = bootstrap({ lang });

    expect(await driver.getLang()).toBe(lang);
  });

  it('should show explanation text when there are no files', async () => {
    const { driver } = bootstrap({ explanationText: 'wow' });
    expect(await driver.getExplanationText()).toBe('wow');
  });

  it('should show single file instead of explanation', async () => {
    const { driver } = bootstrap({
      explanationText: 'wow',
      files: [{ file, state: 'idle', errorMessage: '' }],
    });
    expect(await driver.getSingleFileText()).toBe('meme.png');
  });

  it('should show errorMessage', async () => {
    const { driver } = bootstrap({
      errorMessage: 'bad news',
      explanationText: 'wow',
      files: [{ file, state: 'idle', errorMessage: '' }],
    });
    expect(await driver.getErrorMessage()).toBe('bad news');
  });

  describe('single file error message', () => {
    it('should show as errorMessage prop', async () => {
      const { driver } = bootstrap({
        explanationText: 'wow',
        files: [{ file, state: 'error', errorMessage: 'bad file' }],
      });
      expect(await driver.getErrorMessage()).toBe('bad file');
    });
  });

  describe('multiple files popover', () => {
    it('should show multiple files', async () => {
      const { driver } = bootstrap({
        multiple: true,
        explanationText: 'wow',
        files: [
          {
            file: { name: 'funnyCat.png' } as any,
            state: 'idle',
          },
          { file, state: 'idle' },
        ],
      });
      await driver.openPopover();
      expect(await driver.isPopoverOpen()).toBe(true);
      expect(await driver.getFilesNames()).toEqual([
        'funnyCat.png',
        'meme.png',
      ]);
      expect(await driver.getFilesErrors()).toEqual([undefined, undefined]);
    });

    it('should show files loading', async () => {
      const { driver } = bootstrap({
        multiple: true,
        explanationText: 'wow',
        files: [
          {
            file: { name: 'funnyCat.png' } as any,
            state: 'idle',
          },
          { file, state: 'uploading' },
        ],
      });
      await driver.openPopover();
      expect(await driver.isFileLoading(1)).toBe(true);
    });

    it('should show errors on files', async () => {
      const { driver } = bootstrap({
        multiple: true,
        explanationText: 'wow',
        files: [
          {
            file: { name: 'funnyCat.png' } as any,
            state: 'error',
            errorMessage: 'not funny enough',
          },
          { file, state: 'error', errorMessage: 'bad meme' },
        ],
      });
      await driver.openPopover();
      expect(await driver.getFilesNames()).toEqual([
        'funnyCat.png',
        'meme.png',
      ]);
      expect(await driver.getFilesErrors()).toEqual([
        'not funny enough',
        'bad meme',
      ]);
    });
  });

  describe('onDeleteClick', () => {
    it('should return deleted index when clicking in popover', async () => {
      const onDeleteMock = jest.fn();
      const { driver } = bootstrap({
        multiple: true,
        explanationText: 'wow',
        onDeleteClick: onDeleteMock,
        files: [
          {
            file: { name: 'funnyCat.png' } as any,
            state: 'idle',
          },
          { file, state: 'idle' },
        ],
      });
      await driver.openPopover();
      await driver.clickPopoverDeleteOn(1);
      expect(onDeleteMock).toHaveBeenCalledWith(1);
    });

    it('should return deleted index when clicking on single file delete', async () => {
      const onDeleteMock = jest.fn();
      const { driver } = bootstrap({
        explanationText: 'wow',
        onDeleteClick: onDeleteMock,
        files: [{ file, state: 'idle' }],
      });
      await driver.clickDeleteOnSingleFile();
      expect(onDeleteMock).toHaveBeenCalledWith(0);
    });
  });
});
